/**
 * docsToc — pure helper that derives an anchor-TOC from a help-doc's markdown
 * body (Day 100b.1). For each ATX heading (outside fenced code) it emits an
 * ordered { depth, text, id } entry, assigning the id with a FRESH GithubSlugger
 * per call so the ids deterministically match the heading ids rehype-slug stamps
 * on the rendered article (same lib + same per-doc dedup order → same ids).
 *
 * Pure: no React, no I/O — it tree-shakes out of the arbi-root bundle and is
 * unit-testable. Consumed by the arbi-docs HelpDocs chrome (NOT inside the shared
 * MarkdownView, so the arbi-dev CMS preview gets no TOC). Heading text is reduced
 * to its rendered form (links → link text, images → alt, emphasis/code markers
 * dropped) so github-slugger sees the same text rehype-slug does; nested/HTML
 * inline in headings is out of scope (DOC_STANDARDS §3 help headings are plain).
 */
import GithubSlugger from 'github-slugger';

export interface TocEntry {
  depth: number;
  text: string;
  id: string;
}

// Reduce a raw ATX heading's inline markdown to the rendered text rehype-slug
// would slug: drop the URL from links/images (keep the visible text/alt), unwrap
// inline code, and strip emphasis markers. github-slugger normalises the rest.
function renderedHeadingText(raw: string): string {
  return raw
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // ![alt](url) → alt
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [text](url) → text
    .replace(/`([^`]+)`/g, '$1') // `code` → code
    .replace(/\*\*|__|\*|_/g, '') // bold / italic markers
    .trim();
}

/**
 * Parse the markdown body into an ordered anchor-TOC. ATX headings only
 * (`# … ######`, 0–3 leading spaces); headings inside ``` / ~~~ fenced blocks are
 * skipped — they are not headings (rehype-slug ignores them too, e.g. a shell
 * `# comment` inside a code block). A FRESH GithubSlugger per call means repeated
 * headings dedup as `steps`, `steps-1`, … exactly as rehype-slug does in-document.
 */
export function buildDocsToc(markdown: string): TocEntry[] {
  const slugger = new GithubSlugger();
  const entries: TocEntry[] = [];
  let fence: string | null = null; // active fence marker char ('`' or '~'), or null

  for (const line of markdown.split('\n')) {
    const fenceMatch = line.match(/^ {0,3}(`{3,}|~{3,})/);
    if (fenceMatch) {
      const marker = fenceMatch[1][0];
      if (fence === null) fence = marker;
      else if (marker === fence) fence = null;
      continue;
    }
    if (fence !== null) continue;

    const heading = line.match(/^ {0,3}(#{1,6})\s+(.+?)(?:\s+#+)?\s*$/);
    if (!heading) continue;
    const text = renderedHeadingText(heading[2]);
    if (!text) continue;
    entries.push({ depth: heading[1].length, text, id: slugger.slug(text) });
  }
  return entries;
}
